import { useEffect } from 'react';

/**
 * Hook para detectar resolución de pantalla y aplicar optimizaciones automáticas
 * Específicamente optimizado para mejorar la experiencia en 1920x1080
 */
export const useScreenOptimization = () => {
  useEffect(() => {
    const applyOptimizations = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // Remover clases anteriores
      document.body.classList.remove(
        'screen-1080p',
        'screen-1440p', 
        'screen-4k',
        'compact-mode',
        'height-constrained'
      );

      // Detectar resolución y aplicar optimizaciones (SIN modo compacto automático)
      if (width === 1920 && height === 1080) {
        // Pantalla Full HD exacta
        document.body.classList.add('screen-1080p');
        document.documentElement.style.setProperty('--zoom-compensation', '1');
      } else if (width >= 1900 && width <= 1940 && height >= 1060 && height <= 1100) {
        // Pantalla Full HD con barras del navegador
        document.body.classList.add('screen-1080p');
        document.documentElement.style.setProperty('--zoom-compensation', '1');
      } else if (width >= 2560 && height >= 1440) {
        // Pantalla QHD o superior
        document.body.classList.add('screen-1440p');
        document.documentElement.style.setProperty('--zoom-compensation', '1.1');
      } else if (width >= 3840 && height >= 2160) {
        // Pantalla 4K
        document.body.classList.add('screen-4k');
        document.documentElement.style.setProperty('--zoom-compensation', '1.3');
      } else if (height < 900 && width >= 1200) {
        // Pantalla con altura limitada
        document.body.classList.add('height-constrained');
        document.documentElement.style.setProperty('--zoom-compensation', '0.95');
      }

      // Aplicar compensación por zoom del navegador
      const zoomLevel = Math.round((devicePixelRatio * 100));
      if (zoomLevel > 100) {
        const compensation = 100 / zoomLevel;
        document.documentElement.style.setProperty('--browser-zoom-compensation', compensation.toString());
      } else {
        document.documentElement.style.setProperty('--browser-zoom-compensation', '1');
      }

      // Información para debugging (solo en desarrollo)
        // ...logs de debugging eliminados...
    };

    // Aplicar optimizaciones iniciales
    applyOptimizations();

    // Aplicar optimizaciones cuando cambie el tamaño de ventana
    const handleResize = () => {
      clearTimeout(window.resizeTimeout);
      window.resizeTimeout = setTimeout(applyOptimizations, 250);
    };

    // Detectar cambios de zoom
    const handleZoomChange = () => {
      clearTimeout(window.zoomTimeout);
      window.zoomTimeout = setTimeout(applyOptimizations, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', applyOptimizations);
    
    // Detectar cambios de zoom usando media queries
    const zoomMediaQuery = window.matchMedia('screen and (-webkit-device-pixel-ratio: 1)');
    if (zoomMediaQuery.addEventListener) {
      zoomMediaQuery.addEventListener('change', handleZoomChange);
    } else if (zoomMediaQuery.addListener) {
      // Fallback para navegadores más antiguos
      zoomMediaQuery.addListener(handleZoomChange);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', applyOptimizations);
      
      if (zoomMediaQuery.removeEventListener) {
        zoomMediaQuery.removeEventListener('change', handleZoomChange);
      } else if (zoomMediaQuery.removeListener) {
        zoomMediaQuery.removeListener(handleZoomChange);
      }
      
      clearTimeout(window.resizeTimeout);
      clearTimeout(window.zoomTimeout);
    };
  }, []);
};

/**
 * Hook para obtener información actual de la pantalla
 */
export const useScreenInfo = () => {
  const getScreenInfo = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    isFullHD: window.innerWidth === 1920 && window.innerHeight === 1080,
    isCompactHeight: window.innerHeight < 900,
    zoomLevel: Math.round((window.devicePixelRatio * 100))
  });

  return getScreenInfo;
};

/**
 * Función utilitaria para forzar modo compacto manualmente
 */
export const toggleCompactMode = (force = null) => {
  const isCurrentlyCompact = document.body.classList.contains('compact-mode');
  
  if (force === true || (force === null && !isCurrentlyCompact)) {
    document.body.classList.add('compact-mode');
    localStorage.setItem('portal-compact-mode', 'true');
    return true;
  } else {
    document.body.classList.remove('compact-mode');
    localStorage.setItem('portal-compact-mode', 'false');
    return false;
  }
};

/**
 * Función para aplicar modo compacto según preferencia guardada
 */
export const applyStoredCompactMode = () => {
  const stored = localStorage.getItem('portal-compact-mode');
  if (stored === 'true') {
    document.body.classList.add('compact-mode');
  }
};